import React from 'react';
import type { ApolloCache } from '@apollo/client';

import { ArrowUturnLeftIcon, ArrowsRightLeftIcon, XMarkIcon } from '@heroicons/react/20/solid';
import { UserGroupIcon } from '@heroicons/react/24/outline';
import { messageOfFailure } from '@oysterjs/core/graphql/errors';

import {
  useAddClientArtifactMutation,
  useAddClientFileMutation,
  useGetWorkspaceDocumentClientsQuery,
  useRemoveClientArtifactMutation,
  useRemoveClientFileMutation
} from '~/api/graphql';
import { evictConnectionField } from '~/api/cache';
import { FEATURE_FLAGS, useFlag } from '~/components/feature-flags';
import { type RowAction, RowActionsMenu } from '~/components/row-actions-menu';
import { ChooseClientDialog } from '~/components/workspaces/workspace-client-picker';

type ClientRef = { id: string; name: string };

export type WorkspaceDocument = { kind: 'artifact' | 'file'; id: string; title: string };

const NO_CLIENTS: ClientRef[] = [];

/** The clients in `clients` that are not in `of`. */
const notIn = (clients: ClientRef[], of: ClientRef[]) =>
  clients.filter((client) => !of.some((other) => other.id === client.id));

const names = (clients: ClientRef[]) => clients.map((client) => client.name).join(', ');

/** Which clients each workspace document is on. Issues no query while the Clients tab is off. */
export const useWorkspaceDocumentClients = (workspaceId: string) => {
  const enabled = useFlag(FEATURE_FLAGS.SHOW_CLIENTS_TAB);
  const { data, refetch } = useGetWorkspaceDocumentClientsQuery({
    variables: { workspaceId },
    skip: !enabled,
    // The client pages change these too.
    fetchPolicy: 'cache-and-network'
  });
  const workspaceClients = data?.caraWorkspace?.clients ?? NO_CLIENTS;

  // The top bar's re-read after filing the workspace lands in this query's
  // clients too, and filing moves every document with it. Null until the first
  // answer, which is the baseline rather than a change.
  const filedUnder = data?.caraWorkspace
    ? workspaceClients.map((client) => client.id).join(',')
    : null;
  const seen = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (filedUnder === null || seen.current === filedUnder) return;
    const first = seen.current === null;
    seen.current = filedUnder;
    if (!first) void refetch();
  }, [filedUnder, refetch]);

  const byDocument = React.useMemo(
    () =>
      new Map(
        (data?.caraWorkspace?.documentClients ?? []).map((entry) => [
          entry.documentId,
          entry.clients
        ])
      ),
    [data]
  );

  return {
    // Until the first answer, every document would read as on no client.
    ready: enabled && Boolean(data?.caraWorkspace),
    workspaceClients,
    clientsOf: (documentId: string) => byDocument.get(documentId) ?? NO_CLIENTS,
    refetch
  };
};

/**
 * Attach and remove for either kind of document, answering with an error message
 * or null. The client's own cache-first Documents or Uploads list is evicted, so
 * its next visit reads fresh.
 */
const useDocumentClientWrites = () => {
  const [addArtifact] = useAddClientArtifactMutation();
  const [removeArtifact] = useRemoveClientArtifactMutation();
  const [addFile] = useAddClientFileMutation();
  const [removeFile] = useRemoveClientFileMutation();

  const write = async (
    action: 'add' | 'remove',
    document: WorkspaceDocument,
    client: ClientRef
  ): Promise<string | null> => {
    const update = (cache: ApolloCache<unknown>) =>
      evictConnectionField(cache, document.kind === 'artifact' ? 'artifacts' : 'files', {
        __typename: 'Client',
        id: client.id
      });
    const clientId = client.id;
    const failure =
      action === 'add'
        ? `Could not add this document to ${client.name}.`
        : `Could not remove this document from ${client.name}.`;
    // A request that never reached the server rejects rather than returning errors.
    const result = await (
      document.kind === 'artifact'
        ? (action === 'add' ? addArtifact : removeArtifact)({
            variables: { clientId, artifactId: document.id },
            update
          })
        : (action === 'add' ? addFile : removeFile)({
            variables: { clientId, fileId: document.id },
            update
          })
    ).catch(() => null);
    return result ? messageOfFailure(result, failure) : failure;
  };

  return {
    write,

    /**
     * Attaches first, so a failure part way leaves the document on both clients
     * rather than on neither.
     */
    move: async (document: WorkspaceDocument, from: ClientRef[], to: ClientRef) => {
      const added = await write('add', document, to);
      if (added) return added;
      for (const client of from) {
        const removed = await write('remove', document, client);
        if (removed) return `Added to ${to.name}, but could not remove it from ${client.name}.`;
      }
      return null;
    }
  };
};

/** The line under a document whose clients differ from the workspace's; null when they match. */
export const documentClientNote = (
  clients: ClientRef[],
  workspaceClients: ClientRef[]
): string | null => {
  const others = notIn(clients, workspaceClients);
  const missing = notIn(workspaceClients, clients);
  if (others.length > 0) {
    // Still on the workspace's clients too, which is what a half-finished move leaves.
    const also = workspaceClients.length > 0 && missing.length === 0;
    return `${also ? 'Also on' : 'On'} ${names(others)}`;
  }
  if (missing.length > 0) return `Not on ${names(missing)}`;
  return null;
};

/** The client actions on one document in the workspace's Documents panel. */
export const WorkspaceDocumentClientMenu = ({
  workspaceId,
  document,
  clients,
  workspaceClients,
  onChanged,
  onError
}: {
  workspaceId: string;
  document: WorkspaceDocument;
  /** The clients this document is on now. */
  clients: ClientRef[];
  workspaceClients: ClientRef[];
  onChanged: () => void;
  onError: (message: string | null) => void;
}) => {
  const writes = useDocumentClientWrites();
  const [choosing, setChoosing] = React.useState(false);

  const run = async (write: () => Promise<string | null>) => {
    onError(null);
    onError(await write());
    onChanged();
  };

  const moving = clients.length > 0;
  const choice = moving
    ? {
        label: 'Move to another client…',
        icon: ArrowsRightLeftIcon,
        title: 'Move to another client',
        description: `"${document.title}" leaves ${names(clients)} and appears on the client you choose. It stays in this workspace.`,
        confirmLabel: 'Move'
      }
    : {
        label: 'Add to a client…',
        icon: UserGroupIcon,
        title: 'Add to a client',
        description: `"${document.title}" appears on the client you choose. It stays in this workspace.`,
        confirmLabel: 'Add'
      };

  const actions: RowAction[] = [
    ...clients.map((client) => ({
      label: `Remove from ${client.name}`,
      icon: XMarkIcon,
      onSelect: () => void run(() => writes.write('remove', document, client))
    })),
    ...notIn(workspaceClients, clients).map((client) => ({
      label: `Add back to ${client.name}`,
      icon: ArrowUturnLeftIcon,
      onSelect: () => void run(() => writes.write('add', document, client))
    })),
    { label: choice.label, icon: choice.icon, onSelect: () => setChoosing(true) }
  ];

  return (
    <>
      <RowActionsMenu label={`Client actions for ${document.title}`} actions={actions} />
      <ChooseClientDialog
        open={choosing}
        workspaceId={workspaceId}
        title={choice.title}
        description={choice.description}
        confirmLabel={choice.confirmLabel}
        excludeClientIds={clients.map((client) => client.id)}
        suggest={workspaceClients.length === 0}
        onChoose={async (target) => {
          // With no clients to leave, a move is just the attach.
          const message = await writes.move(document, clients, target);
          onChanged();
          return message;
        }}
        onClose={() => setChoosing(false)}
      />
    </>
  );
};
